<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="900px" persistent scrollable>
    <v-card>
      <v-card-title>
        <span class="headline">إدارة عناصر القائمة المنسدلة</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div class="d-flex justify-space-between align-center mb-4">
          <h3>العناصر</h3>
          <v-btn color="primary" small @click="openAddItemDialog">
            <v-icon left small>mdi-plus</v-icon>
            إضافة عنصر
          </v-btn>
        </div>

        <v-data-table
          :headers="itemHeaders"
          :items="items"
          :loading="loading"
          class="elevation-1"
        >
          <template v-slot:[`item.is_active`]="{ item }">
            <v-chip :color="item.is_active ? 'success' : 'error'" small>
              {{ item.is_active ? 'نشط' : 'غير نشط' }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="primary" small icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="openEditItemDialog(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-pencil</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>تعديل</v-list-item-title>
                </v-list-item>
                <v-list-item @click="deleteItem(item.id)" v-if="!item.deleted_at">
                  <v-list-item-icon>
                    <v-icon>mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>حذف</v-list-item-title>
                </v-list-item>
                <v-list-item @click="restoreItem(item.id)" v-if="item.deleted_at">
                  <v-list-item-icon>
                    <v-icon>mdi-restore</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>استعادة</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- Add/Edit Item Dialog -->
      <v-dialog v-model="itemDialog" max-width="500px" persistent>
        <v-card>
          <v-card-title>
            <span class="headline">{{ editItemMode ? 'تعديل' : 'إضافة' }} عنصر</span>
          </v-card-title>
          <v-card-text>
            <v-form ref="itemForm" v-model="itemValid">
              <v-text-field
                v-model="itemForm.value"
                label="القيمة (Value)"
                :rules="[rules.required]"
                :disabled="editItemMode"
                required
              ></v-text-field>
              <v-text-field
                v-model="itemForm.title"
                label="العنوان"
              ></v-text-field>
              <v-text-field
                v-model.number="itemForm.sort_order"
                label="ترتيب العرض"
                type="number"
                min="0"
              ></v-text-field>
              <v-switch
                v-model="itemForm.is_active"
                label="نشط"
              ></v-switch>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeItemDialog">إلغاء</v-btn>
            <v-btn color="primary" @click="saveItem" :loading="savingItem">حفظ</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400px" persistent>
        <v-card>
          <v-card-title class="headline">تأكيد الحذف</v-card-title>
          <v-card-text>
            هل أنت متأكد من حذف هذا العنصر؟
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDialog = false">إلغاء</v-btn>
            <v-btn color="error" @click="confirmDeleteItem" :loading="loading">حذف</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import Axios from "axios";

export default {
  name: "DropdownItemsDialog",
  props: {
    value: Boolean,
    dropdownId: Number,
  },
  data() {
    return {
      loading: false,
      savingItem: false,
      items: [],
      itemDialog: false,
      editItemMode: false,
      itemValid: false,
      itemForm: {
        id: null,
        dropdown_id: null,
        value: "",
        title: "",
        sort_order: 0,
        is_active: true,
      },
      itemHeaders: [
        { text: "القيمة", value: "value" },
        { text: "العنوان", value: "title" },
        { text: "ترتيب العرض", value: "sort_order" },
        { text: "الحالة", value: "is_active" },
        { text: "العمليات", value: "actions", sortable: false },
      ],
      deleteDialog: false,
      itemToDelete: null,
      rules: {
        required: (v) => !!v || "هذا الحقل مطلوب",
      },
    };
  },
  watch: {
    value(val) {
      if (val && this.dropdownId) {
        this.$nextTick(() => {
          this.fetchItems();
        });
      } else if (!val) {
        this.items = [];
      }
    },
    dropdownId(val) {
      if (val && this.value) {
        this.$nextTick(() => {
          this.fetchItems();
        });
      }
    },
  },
  mounted() {
    if (this.value && this.dropdownId) {
      this.$nextTick(() => {
        this.fetchItems();
      });
    }
  },
  methods: {
    async fetchItems() {
      if (!this.dropdownId) {
        console.error('Dropdown ID is missing');
        return;
      }
      
      this.loading = true;
      try {
        const response = await Axios.post(
          `${this.$store.state.url}/employee/admin/dropdown-items`,
          {
            dropdown_id: this.dropdownId,
          },
          {
            headers: this.request_headers(),
          }
        );
        
        console.log('Dropdown Items Response:', response.data);
        this.items = response.data.payload?.data || [];
      } catch (error) {
        console.error('Error fetching dropdown items:', error);
        this.showPopup("error", error.response?.data?.message || "حدث خطأ");
      } finally {
        this.loading = false;
      }
    },
    openAddItemDialog() {
      this.editItemMode = false;
      this.itemForm = {
        id: null,
        dropdown_id: this.dropdownId,
        value: "",
        title: "",
        sort_order: 0,
        is_active: true,
      };
      this.itemDialog = true;
    },
    openEditItemDialog(item) {
      this.editItemMode = true;
      this.itemForm = {
        id: item.id,
        dropdown_id: item.dropdown_id,
        value: item.value,
        title: item.title,
        sort_order: item.sort_order,
        is_active: item.is_active,
      };
      this.itemDialog = true;
    },
    closeItemDialog() {
      this.itemDialog = false;
      this.$refs.itemForm?.reset();
    },
    async saveItem() {
      if (!this.$refs.itemForm.validate()) return;

      this.savingItem = true;
      try {
        if (this.editItemMode) {
          await Axios.post(
            `${this.$store.state.url}/employee/admin/update-dropdown-item/${this.itemForm.id}`,
            this.itemForm,
            {
              headers: this.request_headers(),
            }
          );
        } else {
          await Axios.post(
            `${this.$store.state.url}/employee/admin/add-dropdown-item`,
            this.itemForm,
            {
              headers: this.request_headers(),
            }
          );
        }

        this.showPopup("success", `تم ${this.editItemMode ? "تعديل" : "إضافة"} العنصر بنجاح`);
        this.closeItemDialog();
        this.fetchItems();
      } catch (error) {
        this.showPopup("error", error.response?.data?.message || "حدث خطأ");
      } finally {
        this.savingItem = false;
      }
    },
    deleteItem(id) {
      this.itemToDelete = id;
      this.deleteDialog = true;
    },
    async confirmDeleteItem() {
      if (!this.itemToDelete) return;

      try {
        await Axios.get(
          `${this.$store.state.url}/employee/admin/delete-dropdown-item/${this.itemToDelete}`,
          {
            headers: this.request_headers(),
          }
        );
        this.showPopup("success", "تم الحذف بنجاح");
        this.deleteDialog = false;
        this.itemToDelete = null;
        this.fetchItems();
      } catch (error) {
        this.showPopup("error", error.response?.data?.message || "حدث خطأ");
        this.deleteDialog = false;
        this.itemToDelete = null;
      }
    },
    async restoreItem(id) {
      try {
        await Axios.post(
          `${this.$store.state.url}/employee/admin/restore-dropdown-item/${id}`,
          {},
          {
            headers: this.request_headers(),
          }
        );
        this.showPopup("success", "تم الاستعادة بنجاح");
        this.fetchItems();
      } catch (error) {
        this.showPopup("error", error.response?.data?.message || "حدث خطأ");
      }
    },
  },
};
</script>
